import { Component, OnInit } from '@angular/core';
import { TechnicalFormComponent } from 'src/app/+grid-application-shared/components/technical-form/technical-form.component';
import { RegistrationOfEnergyProducerTechnicalData } from '../entities/regep-technical-data';
import { Document } from '../../jurisdiction/jurisdiction.domain';
import { GridApplicationDocumentTypes } from 'src/app/+grid-application-shared/entities/enums/grid-application-document-types.enum';
import { RegistrationOfEnergyProducerApplicationDocumentTypes } from '../entities/enums/regepDocumentTypes';
import { RegepOrderProcessService } from '../services/regep-order-process.service';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/services/common.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { FileService } from 'src/app/services/file.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { RegepProcessStatus } from '../entities/enums/regepProcessStatus';
import { RegistrationOfEnergyProducerApplication } from '../entities/regep-application';
import { RegepApplicationService } from '../services/regep-application.service';
import { RegepCommentsService } from '../services/regep-comments.service';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { RegepProvisioningType } from '../entities/enums/regepProvisioningType';
import { toDateOnlyString } from 'src/app/+shared/helpers/date.helper';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-regep-technical-form',
  templateUrl: './regep-technical-form.component.html',
  styleUrls: ['./regep-technical-form.component.scss'],
})
export class RegepTechnicalFormComponent
  extends TechnicalFormComponent<RegistrationOfEnergyProducerApplication>
  implements ComponentCanDeactivate
{
  public technicalData: RegistrationOfEnergyProducerTechnicalData = new RegistrationOfEnergyProducerTechnicalData();
  public isTotalPowerEqualOrHigher: boolean;
  public isCommentTextChanged: boolean;
  public historyLogForm: FormGroup;
  private orderProcessStatus: RegepProcessStatus;

  private storageWarning: boolean = false;
  private photovoltaicWarning: boolean = false;
  private generatorWarning: boolean = false;
  private hasCustomerAddedData: boolean = false;

  constructor(
    fileService: FileService,
    translate: NetzTranslateService,
    commonService: CommonService,
    service: RegepApplicationService,
    commentService: RegepCommentsService,
    errorService: ErrorService,
    router: Router,
    route: ActivatedRoute,
    localStorageService: LocalStorageService,
    environmentService: EnvironmentService,
    translateService: TranslateService,
    private orderProcessService: RegepOrderProcessService
  ) {
    super(
      fileService,
      translate,
      commonService,
      service,
      commentService,
      errorService,
      router,
      route,
      localStorageService,
      environmentService,
      translateService
    );
    this.isTotalPowerEqualOrHigher = commonService.locationData?.totalFeedInPowerIsAboveCentralGridThreshold ?? false;
  }

  get showRegulatoryData() {
    return (
      this.orderProcessStatus >= RegepProcessStatus.EngineeringOk &&
      ((this.isModeratorRole() && this.hasCustomerAddedData) || (this.service as RegepApplicationService).isApplicationCustomer)
    );
  }

  canDeactivate(): boolean {
    return (
      this.preventCanDeactivateCheck ||
      (!this.isTechnicalFormDirty && !this.isLockUnlockButtonClicked && !this.isCommentTextChanged && !this.historyLogForm?.dirty)
    );
  }

  public saveFormData(shouldProceed: boolean) {
    if (
      this.technicalData.feed_in_management !== 'controlAndDemand' ||
      this.technicalData.provisioning_type !== RegepProvisioningType.Shipping
    ) {
      this.technicalData.shipping_address = null;
    }

    if (this.technicalData.preferred_provisioning_date) {
      this.technicalData.preferred_provisioning_date = toDateOnlyString(this.technicalData.preferred_provisioning_date) as any;
    }

    if (this.technicalData.confirmed_provisioning_date) {
      this.technicalData.confirmed_provisioning_date = toDateOnlyString(this.technicalData.confirmed_provisioning_date) as any;
    }

    super.saveFormData(shouldProceed);
  }

  public checkFormValidityInRespectToRole(): boolean {
    if (!this.isModeratorRole()) {
      return this.technicalData.commissioning_of_energy_producer.confirm_commissioning_corresponds_to_technical_data;
    }

    return true;
  }

  protected prepareDocumentsForDetails(documents: Document[]): void {
    super.prepareDocumentsForDetails(documents);

    this.technicalData.feed_in_controllability_contract =
      documents.find(d => d.sk_document_type === GridApplicationDocumentTypes.FeedInControllabilityContract) ||
      new Document(GridApplicationDocumentTypes.FeedInControllabilityContract);

    this.technicalData.list_of_data_points =
      documents.find(d => d.sk_document_type === GridApplicationDocumentTypes.ListOfDataPoints) ||
      new Document(GridApplicationDocumentTypes.ListOfDataPoints);

    this.technicalData.commissioning_of_energy_producer.photo_documentation_of_installed_generators_and_converters =
      documents.find(
        d =>
          d.sk_document_type === RegistrationOfEnergyProducerApplicationDocumentTypes.PhotoDocumentationOfInstalledGeneratorsAndConverters
      ) || new Document(RegistrationOfEnergyProducerApplicationDocumentTypes.PhotoDocumentationOfInstalledGeneratorsAndConverters);

    this.technicalData.commissioning_of_energy_producer.request_for_meter_mounting =
      documents.find(d => d.sk_document_type === RegistrationOfEnergyProducerApplicationDocumentTypes.RequestForMeterMounting) ||
      new Document(RegistrationOfEnergyProducerApplicationDocumentTypes.RequestForMeterMounting);
  }

  protected prepareDetails(data: RegistrationOfEnergyProducerTechnicalData): void {
    super.prepareDetails(data);

    const commissioningOfEnergyProducer = data.commissioning_of_energy_producer;
    commissioningOfEnergyProducer.commissioning_date = commissioningOfEnergyProducer.commissioning_date
      ? new Date(commissioningOfEnergyProducer.commissioning_date)
      : null;
    commissioningOfEnergyProducer.commissioning_date_unknown = JSON.parse(commissioningOfEnergyProducer.commissioning_date_unknown + '');
    commissioningOfEnergyProducer.confirm_commissioning_corresponds_to_technical_data = JSON.parse(
      commissioningOfEnergyProducer.confirm_commissioning_corresponds_to_technical_data + ''
    );

    data.ripple_control_receiver_preferred_pickup_date = data.ripple_control_receiver_preferred_pickup_date
      ? new Date(data.ripple_control_receiver_preferred_pickup_date)
      : null;

    data.ripple_control_receiver_confirmed_pickup_date = data.ripple_control_receiver_confirmed_pickup_date
      ? new Date(data.ripple_control_receiver_confirmed_pickup_date)
      : null;

    const provisioningOfCurrentTransformers = data.provisioning_of_current_transformers;
    provisioningOfCurrentTransformers.has_technical_reasons = JSON.parse(provisioningOfCurrentTransformers.has_technical_reasons + '');

    provisioningOfCurrentTransformers.preferred_date_for_pickup = provisioningOfCurrentTransformers.preferred_date_for_pickup
      ? new Date(provisioningOfCurrentTransformers.preferred_date_for_pickup)
      : null;

    provisioningOfCurrentTransformers.confirmed_date_for_pickup = provisioningOfCurrentTransformers.confirmed_date_for_pickup
      ? new Date(provisioningOfCurrentTransformers.confirmed_date_for_pickup)
      : null;

    data.preferred_provisioning_date = data.preferred_provisioning_date ? new Date(data.preferred_provisioning_date) : null;

    data.confirmed_provisioning_date = data.confirmed_provisioning_date ? new Date(data.confirmed_provisioning_date) : null;
  }

  protected prepareDocumentsForUpload(outputTechData: RegistrationOfEnergyProducerTechnicalData): Document[] {
    const documents = super.prepareDocumentsForUpload(outputTechData);

    if (this.technicalData.commissioning_of_energy_producer.photo_documentation_of_installed_generators_and_converters?.file) {
      documents.push(this.technicalData.commissioning_of_energy_producer.photo_documentation_of_installed_generators_and_converters);
    }

    if (this.technicalData.commissioning_of_energy_producer.request_for_meter_mounting?.file) {
      documents.push(this.technicalData.commissioning_of_energy_producer.request_for_meter_mounting);
    }

    if (this.technicalData.feed_in_controllability_contract?.file) {
      documents.push(this.technicalData.feed_in_controllability_contract);
    }

    return documents;
  }

  protected loadOrderProcess(): void {
    this.preventLeak(this.orderProcessService.orderProcess$).subscribe(orderProcess => {
      this.setEditabilityBasedOnProcessStatus(orderProcess.status);
      this.orderProcessStatus = orderProcess.status;
      this.hasCustomerAddedData = orderProcess.customerAddedData;
    });

    this.orderProcessService.loadOrderProcess(this.applicationId);
  }

  protected proceed(): void {
    let redirectUrl = this.isDetailsView
      ? `/${this.route.snapshot.data.module}/personal-data/${this.applicationId}`
      : `/${this.route.snapshot.data.module}/personal-data`;

    if (this.showRegulatoryData) {
      redirectUrl = this.isDetailsView
        ? `/${this.route.snapshot.data.module}/regulatory-data/${this.applicationId}`
        : `/${this.route.snapshot.data.module}/regulatory-data`;
    }

    this.preventCanDeactivateCheck = true;
    this.router.navigate([redirectUrl]);
  }

  private setEditabilityBasedOnProcessStatus(status: RegepProcessStatus): void {
    const technicianRoleEditableStatuses = [RegepProcessStatus.EngineeringOpenIssue];
    const customerRoleEditableStatuses = [
      RegepProcessStatus.RegulationsOpenIssue,
      RegepProcessStatus.EngineeringOk,
      RegepProcessStatus.EngineeringOpenIssue,
    ];
    const internalRoleEditableStatuses = [
      RegepProcessStatus.EngineeringRequestUpdated,
      RegepProcessStatus.RequestSent,
      RegepProcessStatus.RegulationsRequestUpdated,
      RegepProcessStatus.CustomerDataAdded,
    ];

    this.editabilityBasedOnProcessStatus.technicianRole = technicianRoleEditableStatuses.includes(status);
    this.editabilityBasedOnProcessStatus.customerRole = customerRoleEditableStatuses.includes(status);
    this.editabilityBasedOnProcessStatus.internalRoles = internalRoleEditableStatuses.includes(status);
  }

  public get producerTotalPowerText(): string {
    if (this.technicalForm?.controls?.total_feed_in_power?.disabled) {
      return this.translate.translateString('grid-comp.producer-total-power');
    }

    return this.isTotalPowerEqualOrHigher
      ? this.translate.translateString('grid-comp.producer-total-power-high')
      : this.translate.translateString('grid-comp.producer-total-power-less');
  }

  public storageWarningsVisible($event: boolean) {
    this.storageWarning = $event;
  }

  public photovoltaicWarningsVisible($event: boolean) {
    this.photovoltaicWarning = $event;
  }

  public generatorWarningsVisible($event: boolean) {
    this.generatorWarning = $event;
  }

  public warningsVisible(): boolean {
    return this.storageWarning || this.photovoltaicWarning || this.generatorWarning;
  }
}
